import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {CircularProgress, Dialog} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {enqueueSnackbar} from 'notistack';
import {isAuthRedirectError} from '../../../shared/services/auth.service';
import {logError} from '../../../shared/services/logger.service';
import {getCompany} from '../../../shared/services/company.service';
import RealTimeService from '../../../shared/services/real-time.service';
import {usePermissions} from '../../../shared/permissions/PermissionContext';
import {PermissionCode} from '../../../shared/permissions/permissions';
import {ExternalCompanyDto} from '../../../shared/dtos/company.dto';
import {EstimateWithComponentsDto} from '../../../shared/dtos/estimate-with-components.dto';
import {EstimateStatus, EstimateStatusType} from '../../../shared/dtos/estimate.dto';
import {EstimateCostItemDto} from '../../../shared/dtos/estimate-cost-item.dto';
import {EstimateCrewMemberDto} from '../../../shared/dtos/estimate-crew-member.dto';
import {EstimateTreeDto, WorkItemType} from '../../../shared/dtos/estimate-tree.dto';
import {TreeOperationType} from '../../../shared/dtos/estimate-tree-operation.dto';
import {applyJobPricing, jobPricingTotal} from '../../../shared/pricing/job-pricing';
import {nextOpenLocation} from '../../../shared/misc/location-helpers';
import {
  ActionBar, CompanyBranding, HeaderButton, RecordMapLayout, RecordMessage, RecordPage, RedButton, TitleRow, useRecordMapLayout,
} from '../../../shared/components/record/RecordKit';
import {ClientCard, ClientEditCard} from '../../client/profile/ClientCard';
import {useClientEditing} from '../../client/profile/useClientEditing';
import {CreateQuestion} from '../../client/profile/contact-entries';
import {AppointmentInfoCard} from '../../appointment/record/AppointmentInfoCard';
import {parseApptTime} from '../../appointment/record/appointment-time';
import {getAppointments, updateAppointment} from '../../appointment/appointment.service';
import {AppointmentDto} from '../../appointment/dto/appointment.dto';
import {getMessageContent, sendCommunication} from '../../../components/communications';
import {getCrewCandidates} from '../../job/services/crew-member.service';
import {CrewCandidateDto} from '../../job/dtos/company-crew-member.dto';
import {getAllEquipment} from '../../equipment/services/equipment.service';
import {
  approveEstimateWithSignature, getAiEstimationStatus, getEstimate, refreshEstimatePricingRules, regenerateAiSuggestions, updateEstimateStatus,
} from '../estimate.service';
import {setEstimateCrewMembers} from '../estimate-crew-member.service';
import {createCostItem, deleteCostItem, updateCostItem} from '../estimate-cost-item.service';
import JobSiteMap from './map/JobSiteMap';
import {localIds, useSiteMap} from './map/useSiteMap';
import {GatePanel, gatePanelActions} from './map/SiteMapPanels';
import {ClosesOutOfSight} from './ClosesOutOfSight';
import {DrawableImage, PHOTO_DIALOG_PAPER} from '../DrawableImage';
import {PhotoViewerWithEditor} from '../../../shared/components/PhotoViewerWithEditor';
import {PhotoEdit} from '../../../shared/services/image-service';
import {formatMinutes, minimumTopUps, money, rowCost, wallTimes} from './record-math';
import {stumpGrindingSeedNote, WORK_ITEM_LABEL} from './operation-schema';
import {useWorkItems, WorkItems} from './useWorkItems';
import {useAutosaveLanes} from './useAutosave';
import WorkItemCard, {crewCaption, RecordContext, WorkItemJob} from './WorkItemCard';
import WorkItemEdit, {TimerPill} from './WorkItemEdit';
import CrewCard, {CrewPickerDialog, rosterOf} from './CrewCard';
import {laborTotalRefusal, missingPriceReason, parentStepRefusal} from './parent-adjust';
import {hideGatePhotoConfirm, Photo, photosOf} from './PhotoPager';
import {WorkOrderDto, WorkOrderStatus, WorkOrderStatusType} from '../../work-order/dtos/work-order.dto';
import {
  clearWorkOrderSchedule, completeWorkOrder, createJobInvoice, getActiveInvoiceId, getWorkOrder, getWorkOrders, saveJobAllocation,
  updateWorkOrderSchedule, updateWorkOrderStatus,
} from '../../work-order/work-order.service';
import {useJobClocks} from './useJobClocks';
import {
  ALLOCATION_HIDDEN, Allocation, allocateJobTime, currentAllocation, equipmentOverUnder, finishJobChecks, isCancelledJob, isCompletedJob,
  isEditableJob, isOnHoldJob, isOpenJob, JobActionOutcome, jobActionOutcome, jobEquipmentOverUnder, jobHasSchedule, moveWorkItemTime,
  overUnderTag, pooledCrewMinutes, proportionalAllocation, REOPEN_JOB_CONFIRM_MESSAGE, sameAllocation, scheduleSlotLabel, secondsToMinutes,
  seesActuals, storedAllocation,
} from './job-math';
import {
  CrewClock, FinishJobFooter, JobTimeList, ReviewFinishFooter, ReviewJobBanner, ScheduledCard, ScheduleDialog, TimeAllocationCard,
} from './JobPieces';
import {ActualReadout, OverUnderTag} from './JobReadouts';
import {InvoiceDto} from '../../invoice/dtos/invoice.dto';
import {getInvoice, recordInvoicePayment} from '../../invoice/invoice.service';
import {customerInvoiceTotals, invoiceStatusLabel, invoiceStatusMoveNotice, isInvoiceEditable, paymentStanding} from './invoice-math';
import {
  InvoiceActionBar, InvoiceFollowUpsLine, InvoiceNoticeDialog, InvoicePaymentSection, InvoiceSendButtons, InvoiceStandingLines,
} from './InvoicePieces';
import {WorkPlanEdit} from './WorkItemEdit';
import {
  AddWorkItemRow, ComparablesCard, CostItemsEdit, EditableJobTotal, FooterCard, SendBlock, SignatureStep, SimpleDialog, TotalRow,
} from './EstimateFooter';
import {EstimateFollowUpsLine} from './EstimateFollowUps';
import {CustomerEstimateTotal, CustomerInvoiceTotals, PayBlock} from './CustomerPieces';
import '../../client/profile/ClientProfile.css';
import '../../appointment/record/AppointmentPage.css';
import './EstimateRecord.css';

/*
 * The estimate's own page (SQ-596): Android's record screen with an estimate
 * on it (clients/ClientDetailScreen.kt and sections/estimate), on the record
 * kit's layout (SQ-574). From the top: the status and Edit / Save in the
 * green bar; Preview, Reopen Est and Cancel; the title, the company, the
 * client card (SQ-575) and Appointment Info (SQ-595); the crew; the work
 * items; in edit mode the add menu and the cost items; the Job Total; the
 * footer for the status; and the site counts. The job site map sits beside
 * the record from 800px and behind the MAP tab below that.
 *
 * Every change saves the way Android's does (useWorkItems.ts): structural
 * ones at once, field edits and steppers after a quiet moment. Save sends
 * whatever is still waiting and locks the page. An estimate in progress
 * opens unlocked, as on Android, and either way entering edit mode prices
 * an unapproved estimate at the company's current pricing rules (SQ-283).
 *
 * Built once for the estimate, the job (SQ-597) and the invoice (SQ-598).
 * Once the estimate is approved it is a job, and the page is the job's: its
 * own status and bar (Schedule, Hold, Cancel), the SCHEDULED card, the
 * crew's clocks, Finish the Job and its review, and on a completed job what
 * the time came to and whether it was bid right (job-math.ts, JobPieces.tsx).
 *
 * Once the job has an invoice the page is the invoice's (SQ-598), as on
 * Android: its status, Add Payment / Go to Send, its number and
 * dates, where the payments stand under the Job Total, and the payment and
 * send block in place of Reopen the Job / Create the Invoice. The body stays
 * the completed job. Edit needs invoices.edit, and an edit or a payment that
 * moves it between Paid and not says so (invoice-math.ts, InvoicePieces.tsx).
 *
 * Preview (SQ-337) is the customer's copy of an estimate or an invoice, as
 * their link shows it (pages/customer/CustomerRecordPage.tsx): the status,
 * the title and number without dates, the company, the client's primary
 * lines, and each work item's photos, site line, measurements, operations
 * with their notes and the Work Plan's first section; then the estimate's
 * total, or the invoice's total, paid and balance and Pay. The bar holds
 * only Admin View. The signature is reached only through Customer Preview
 * and Confirm. A job has no Preview: customers have no jobs.
 */

const NO_PICKS = {};

/** Android's prettyStatusName: no "Estimate" prefix, the words split, "Canceled" with one L. */
export const recordStatusLabel = (status: string) =>
  status.replace(/^Estimate/, '').replace(/([a-z])([A-Z])/g, '$1 $2').trim().replace('Cancelled', 'Canceled');

const AWAITING_CONFIRM: EstimateStatusType[] = [EstimateStatus.EstimateCompleted, EstimateStatus.EstimatePendingApproval, EstimateStatus.EstimatePendingReSend];
const ESTIMATE_PHASE: EstimateStatusType[] = [EstimateStatus.EstimateInProgress, ...AWAITING_CONFIRM, EstimateStatus.EstimateCancelled];

const errorText = (e: unknown, fallback: string) => e instanceof Error && e.message ? e.message : fallback;

/** Beside the record the map is already open: the line only applies while MAP swaps to it (under 800). */
const MapFirstNote = () => {
  const layout = useRecordMapLayout();
  return layout.beside ? null : <div className="er-footer-note">Map out the job site first: tap MAP on the left.</div>;
};

export interface EstimateRecordPageProps {
  estimateId: number;
  /** Opened as a job (/jobs/12): its work order. An approved estimate opened as an estimate finds its own. */
  workOrderId?: number;
  /** Opened as an invoice (/invoices/7): the invoice, already read. A job opened any other way finds its own. */
  invoice?: InvoiceDto;
  /** Something the list behind the page shows changed (status, total). */
  onChanged?: () => void;
}

/** Statuses an estimate has before it is a job. */
const PRE_JOB: string[] = [EstimateStatus.EstimateNotStarted, ...ESTIMATE_PHASE, EstimateStatus.EstimateDeleted];

type JobDialog =
  | {kind: 'schedule'}
  | {kind: 'reopen'}
  | {kind: 'confirm'; message: string; status: WorkOrderStatusType}
  | {kind: 'notice'; message: string}
  | {kind: 'price'}
  | {kind: 'pool'; id: number; seconds: number}
  | {kind: 'reset'}
  | {kind: 'remove'; id: number; name: string; minutes: number; running: boolean};

const EstimateRecordPage = ({estimateId, workOrderId, invoice: openedInvoice, onChanged}: EstimateRecordPageProps) => {
  const navigate = useNavigate();
  const {hasPermission} = usePermissions();
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  const changed = useCallback(() => changedRef.current?.(), []);
  const report = useCallback((message: string) => enqueueSnackbar(message, {variant: 'error', autoHideDuration: 5000}), []);

  const [estimate, setEstimate] = useState<EstimateWithComponentsDto>();
  const [loadError, setLoadError] = useState<string>();
  const [company, setCompany] = useState<ExternalCompanyDto>();
  const [appointment, setAppointment] = useState<AppointmentDto>();
  const [apptTime, setApptTime] = useState<Date>();
  const [apptNotes, setApptNotes] = useState('');
  const [infoOpen, setInfoOpen] = useState(false);
  // SQ-660: the crew picker opened from a timer with no crew.
  const [crewPickerOpen, setCrewPickerOpen] = useState(false);
  const [additionalOpen, setAdditionalOpen] = useState(false);
  const [costItems, setCostItems] = useState<EstimateCostItemDto[]>([]);
  const [crewRows, setCrewRows] = useState<EstimateCrewMemberDto[]>([]);
  const [candidates, setCandidates] = useState<CrewCandidateDto[]>([]);
  const [selectedCrew, setSelectedCrew] = useState<number[]>([]);

  const [editing, setEditing] = useState(false);
  const [customerView, setCustomerView] = useState(false);
  const [signing, setSigning] = useState(false);
  const [working, setWorking] = useState(false);
  const [sentTick, setSentTick] = useState(0);
  // SQ-745: the gate just added, whose panel is open under the add row (Android's openGateId).
  const [openGateId, setOpenGateId] = useState<number>();

  // A work item's photos in the shared viewer (SQ-657), from the one tapped; Edit Picture on the card opens the editor directly.
  const [viewing, setViewing] = useState<{treeId: number; start: number}>();
  const [photo, setPhoto] = useState<{photo: Photo; treeId: number}>();
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [dialog, setDialog] = useState<'cancel' | 'ai-running' | 'ai-crew' | 'manual-confirm' | 'pending' | null>(null);
  const [ratePrompt, setRatePrompt] = useState<CrewCandidateDto>();

  // The job (SQ-597): its work order, the review state, the invoice it may already have.
  const [workOrder, setWorkOrder] = useState<WorkOrderDto>();
  const [review, setReview] = useState(false);
  const [allocationEdits, setAllocationEdits] = useState<Allocation | null>(null);
  const [useStored, setUseStored] = useState(true);
  const [invoiceId, setInvoiceId] = useState<number | undefined>(openedInvoice?.id);
  // The invoice itself (SQ-598): once it has loaded the page is the invoice's.
  const [invoice, setInvoice] = useState<InvoiceDto | undefined>(openedInvoice);
  const [invoiceNotice, setInvoiceNotice] = useState<string>();
  // An invoiced job opened as a job may still turn out to be an invoice: until the lookup is answered it shows neither.
  const [invoiceLookupDone, setInvoiceLookupDone] = useState(!!openedInvoice);
  // Likewise an approved estimate until its work order is found (or found missing).
  const [jobLookupDone, setJobLookupDone] = useState(false);
  const paymentRef = useRef<HTMLDivElement>(null);
  const sendRef = useRef<HTMLDivElement>(null);
  const [jobDialog, setJobDialog] = useState<JobDialog | null>(null);
  const priceConfirmed = useRef(false);
  const pendingPrice = useRef<(() => void) | null>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  const items = useWorkItems(navigate, estimateId, report);
  const clocks = useJobClocks(navigate, workOrder?.id, estimateId, report);
  const workOrderRef = useRef(workOrder);
  workOrderRef.current = workOrder;
  const lanes = useAutosaveLanes(report);
  const canEdit = hasPermission(PermissionCode.EstimatesEdit);
  const canEditClient = hasPermission(PermissionCode.ClientsEdit);
  const clientEditing = useClientEditing(estimate?.clientId, {editing: editing && !customerView, onError: report, onChanged: changed});
  const {client, fields, entries, contacts, contactsRead, rating} = clientEditing;

  const status = estimate?.status;
  const job = !!workOrder;
  const woStatus = workOrder?.status;
  const jobCompleted = job && isCompletedJob(woStatus);
  const jobOpen = job && isOpenJob(woStatus);
  const cancelled = job ? isCancelledJob(woStatus) : status === EstimateStatus.EstimateCancelled;
  const estimatePhase = !job && !!status && ESTIMATE_PHASE.includes(status);
  const canEditJob = hasPermission(PermissionCode.JobsEdit);
  const invoicePage = !!invoice;
  // Android's recordPending: an invoiced job whose invoice has not loaded yet shows no status, an empty bar and a bare title.
  const invoicePending = job && !invoice && !invoiceLookupDone && hasPermission(PermissionCode.InvoicesView)
    && (woStatus === WorkOrderStatus.Invoiced || woStatus === WorkOrderStatus.PendingReInvoice);
  const invoiceEditable = !!invoice && isInvoiceEditable(invoice.status, hasPermission(PermissionCode.InvoicesEdit));
  // An invoice is edited like a completed job, by whoever has invoices.edit (SQ-401).
  const canUnlock = invoicePage ? invoiceEditable
    : job ? canEditJob && isEditableJob(woStatus) : canEdit && estimatePhase && !cancelled;

  // ---- Load ------------------------------------------------------------------------------------

  /** SQ-283: an unapproved estimate entering edit mode takes the company's current pricing rules; only the pricing fields are taken from the re-read. */
  const refreshPricing = useCallback(async () => {
    try {
      if (!(await refreshEstimatePricingRules(navigate, estimateId))) return;
      const fresh = await getEstimate(navigate, estimateId);
      setEstimate(e => e && {
        ...e, pricingRules: fresh.pricingRules, travelMiles: fresh.travelMiles, smallJobRateAmount: fresh.smallJobRateAmount,
        minimumTopUpAmount: fresh.minimumTopUpAmount, travelAmount: fresh.travelAmount, totalCost: fresh.totalCost,
      });
    } catch (e) {
      if (!isAuthRedirectError(e)) logError('Pricing rules refresh failed', {estimateId}, e as Error);
    }
  }, [navigate, estimateId]);

  const adopt = useCallback((loaded: EstimateWithComponentsDto) => {
    setEstimate(loaded);
    setCostItems(loaded.costItems ?? []);
    setCrewRows(loaded.crewMembers ?? []);
    setSelectedCrew((loaded.crewMembers ?? []).map(m => m.companyCrewMemberId));
    items.initialize(loaded);
  }, [items]);

  useEffect(() => {
    let active = true;
    getCompany(navigate).then(c => active && setCompany(c)).catch(() => undefined);
    getEstimate(navigate, estimateId)
      // Start an Estimate opens one not started yet: opening it starts it (EstimateSectionController.start).
      .then(async loaded => loaded.status === EstimateStatus.EstimateNotStarted && canEdit
        ? updateEstimateStatus(navigate, estimateId, {status: EstimateStatus.EstimateInProgress})
          .then(() => getEstimate(navigate, estimateId)).catch(() => loaded)
        : loaded)
      .then(loaded => {
        if (!active) return;
        adopt(loaded);
        // An approved estimate is a job: the page is the work order's (SQ-597).
        if (workOrderId !== undefined || !PRE_JOB.includes(loaded.status)) {
          (workOrderId !== undefined
            ? getWorkOrder(navigate, workOrderId)
            : getWorkOrders(navigate).then(all => all.find(w => w.estimateId === loaded.id)))
            .then(found => {
              if (!active) return;
              setJobLookupDone(true);
              if (!found) return;
              setWorkOrder(found);
              // Opened as an invoice, the page already knows its invoice.
              if (!openedInvoice && isCompletedJob(found.status) && hasPermission(PermissionCode.InvoicesView)) {
                getActiveInvoiceId(navigate, found.id)
                  .then(id => {
                    if (!active) return;
                    setInvoiceId(id);
                    if (id === undefined) setInvoiceLookupDone(true);
                  })
                  .catch(() => active && setInvoiceLookupDone(true));
              }
            })
            .catch(e => {
              if (!active || isAuthRedirectError(e)) return;
              logError('Error getting the job', {estimateId}, e as Error);
              setLoadError(`Couldn't load this job: ${e}`);
            });
        }
        const inProgress = loaded.status === EstimateStatus.EstimateInProgress;
        // An estimate being worked on opens unlocked, with Appointment Info open; anything further along opens saved.
        setInfoOpen(inProgress);
        if (inProgress && canEdit) {
          setEditing(true);
          void refreshPricing();
        }
        getAppointments(navigate)
          .then(all => {
            const found = all.find(a => a.estimateId === loaded.id);
            if (!active || !found) return;
            setAppointment(found);
            setApptTime(parseApptTime(found.time));
            setApptNotes(found.notes ?? '');
          })
          .catch(() => undefined);
      })
      .catch(e => {
        if (!active || isAuthRedirectError(e)) return;
        logError('Error getting estimate', {estimateId}, e as Error);
        setLoadError(`Couldn't load this estimate: ${e}`);
      });
    getCrewCandidates(navigate).then(c => active && setCandidates(c)).catch(() => undefined);
    getAllEquipment(navigate).then(e => active && items.setCompanyEquipment(e)).catch(() => undefined);
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId, navigate]);

  // The invoice, once the job is known to have one (or the page was opened as it).
  useEffect(() => {
    if (invoiceId === undefined || invoiceId === invoice?.id || !hasPermission(PermissionCode.InvoicesView)) return undefined;
    let active = true;
    getInvoice(navigate, invoiceId)
      .then(found => active && setInvoice(found))
      .catch(e => {
        if (!active || isAuthRedirectError(e)) return;
        setInvoiceLookupDone(true);
        logError('Error getting the invoice', {invoiceId}, e as Error);
        setLoadError(`Couldn't load this invoice: ${e}`);
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, navigate]);

  // SQ-712: AI pricing is per company and off until SavaQuan turns it on; until the company has loaded it counts as off.
  const aiPricing = company?.aiPricingEnabled === true;

  // AI prices arrive over SignalR (SQ-525): the operation's status at once, its minutes and price with the tree.
  useEffect(() => {
    RealTimeService.getInstance().onAiEstimatedCostStatusUpdated((id, treeId, opId, aiStatus) => {
      if (id === estimateId && aiPricing) items.applyAiStatus(treeId, opId, aiStatus);
    });
  }, [estimateId, items, aiPricing]);

  // ---- Saving -------------------------------------------------------------------------------------

  /** Sends every waiting change: work items, cost items, the crew, the appointment. */
  const flushAll = useCallback(async () => {
    await Promise.all([items.flush(), lanes.flush(), clocks.flush()]);
  }, [items, lanes, clocks]);

  /** Runs one of the page's actions with the buttons held. */
  const run = async (failure: string, action: () => Promise<void>) => {
    if (working) return;
    setWorking(true);
    try {
      await action();
    } catch (e) {
      if (!isAuthRedirectError(e)) {
        logError(failure, {estimateId}, e as Error);
        report(errorText(e, failure));
      }
    } finally {
      setWorking(false);
    }
  };

  const transition = async (target: EstimateStatusType) => {
    await flushAll();
    await updateEstimateStatus(navigate, estimateId, {status: target});
    setEstimate(e => e && {...e, status: target});
    changed();
  };

  /** The estimate moved on server-side (approval made it a job): read it again. */
  const reload = async () => adopt(await getEstimate(navigate, estimateId));

  // ---- The job (SQ-597) -------------------------------------------------------------------------------

  const roster = rosterOf(candidates, crewRows);
  const nameOf = (id: number) => roster.find(m => m.id === id)?.name ?? crewRows.find(m => m.companyCrewMemberId === id)?.name ?? 'Crew';
  // The crew's locked rates, for when a member joins the job's crew.
  clocks.setMemberInfo(Object.fromEntries(crewRows.map(m => [m.companyCrewMemberId, {hourlyRate: m.hourlyRate}])));
  // SQ-745: jobs.edit or jobs.edit_crew_hours run every clock; jobs.timers alone (Level 1) only the person's own, as the server allows.
  const runsEveryClock = hasPermission(PermissionCode.JobsEdit) || hasPermission(PermissionCode.JobsEditCrewHours);
  const ownCrewIds = new Set(clocks.jobCrew.filter(m => m.isYou).map(m => m.companyCrewMemberId));
  const crewClocks: CrewClock[] = selectedCrew.map(id => ({
    id, name: nameOf(id), seconds: clocks.secondsOf(id), running: clocks.isRunning(id),
    controllable: runsEveryClock || ownCrewIds.has(id),
  }));
  const clockedMinutes = pooledCrewMinutes(crewClocks.map(c => c.seconds));
  // SQ-524: a job from before per-crew clocks has its time only on the job-length timer; a completed one uses it (wall × crew).
  const pool = jobCompleted && clockedMinutes === 0
    ? secondsToMinutes(clocks.legacyJobSeconds) * Math.max(1, selectedCrew.length) : clockedMinutes;
  const rate = items.state.laborRatePerHour;
  // SQ-636, as the record's read says: its prices and estimated time were hidden from this person
  // (Level 1 crew), or only its actual times (a Crew Leader). Both apps follow the same two flags.
  const pricesHidden = estimate?.pricesMasked === true;
  const actualsShown = seesActuals(estimate);

  /** Hand-placed time stands against the current crew total: moved on the cards, or saved by a review. */
  const allocationHeld = () => allocationEdits !== null
    || (jobCompleted && useStored && items.trees.some(t => t.operations.some(o => o.actualsOrigin === 'review')));

  /**
   * A job's total moves only on purpose: the first change in an Edit session that moves it asks (SQ-530).
   * Where its prices were hidden from the person it is refused at once, before anything is asked (SQ-636).
   */
  const priceChange = (change: () => void) => {
    if (items.refusesTimeAndPrice()) return;
    if (!job || priceConfirmed.current) {
      change();
      return;
    }
    pendingPrice.current = change;
    setJobDialog({kind: 'price'});
  };

  /** Runs a status move; a hold or a cancel first stops the clocks and saves what is waiting (they are work that happened). */
  const setJobStatus = (target: WorkOrderStatusType) => void run('Failed to update the job', async () => {
    if (!workOrder) return;
    const closes = target === WorkOrderStatus.OnHold || target === WorkOrderStatus.Cancelled;
    if (closes) {
      await clocks.stopAll();
      await flushAll();
    }
    const updated = await updateWorkOrderStatus(navigate, workOrder.id, {status: target});
    setWorkOrder(w => w && {...w, ...updated, estimateData: w.estimateData});
    if (closes) {
      setEditing(false);
      setReview(false);
      setAllocationEdits(null);
    }
    changed();
  });

  const onJobAction = (outcome: JobActionOutcome | null) => {
    if (!outcome) return;
    switch (outcome.kind) {
      case 'open-schedule': setJobDialog({kind: 'schedule'}); break;
      case 'confirm-reopen': setJobDialog({kind: 'reopen'}); break;
      case 'apply': setJobStatus(outcome.status); break;
      case 'confirm': setJobDialog({kind: 'confirm', message: outcome.message, status: outcome.status}); break;
      case 'blocked': setJobDialog({kind: 'notice', message: outcome.message}); break;
    }
  };
  const hasSchedule = jobHasSchedule(workOrder?.scheduledDate, workOrder?.scheduledStartTime);

  const saveSchedule = (date: string | null, time: string | null, clear = false) => void run('Failed to save the schedule', async () => {
    if (!workOrder) return;
    const updated = clear
      ? await clearWorkOrderSchedule(navigate, workOrder.id)
      : await updateWorkOrderSchedule(navigate, workOrder.id, {scheduledDate: date, scheduledStartTime: time});
    setWorkOrder(w => w && {...w, ...updated, estimateData: w.estimateData});
    changed();
  });

  /** Reopen: back to Confirmed; everything recorded stays, the saved split comes off and finishing spreads the time anew. */
  const reopenJob = () => void run('Failed to reopen job', async () => {
    if (!workOrder) return;
    if (invoiceId !== undefined) {
      report('This job already has an invoice.');
      return;
    }
    const updated = await updateWorkOrderStatus(navigate, workOrder.id, {status: WorkOrderStatus.Confirmed});
    setWorkOrder(w => w && {...w, ...updated, estimateData: w.estimateData});
    setReview(false);
    setAllocationEdits(null);
    setUseStored(true);
    items.refreshAllTrees();
    changed();
    enqueueSnackbar('Job reopened.', {variant: 'success', autoHideDuration: 3000});
  });

  /** The crew's hours: under hand-placed time it asks first, then the time spreads evenly again. */
  const setCrewSeconds = (id: number, seconds: number, confirmed = false) => {
    if (!confirmed && allocationHeld()) {
      setJobDialog({kind: 'pool', id, seconds});
      return;
    }
    if (confirmed) {
      setAllocationEdits(null);
      setUseStored(false);
    }
    clocks.setSeconds(id, seconds);
  };

  const scrollToBanner = () => window.setTimeout(() => bannerRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'}), 50);

  /** Yes, Review Job Details: saves what is waiting, stops every clock (banking its time) and opens review. */
  const enterReview = () => void run('Failed to stop the timers', async () => {
    await flushAll();
    await clocks.stopAll();
    setAllocationEdits(null);
    setReview(true);
    scrollToBanner();
  });

  /** Go Back to Job: the job as it was; the time moved on the tan cards is dropped. */
  const exitReview = () => {
    setReview(false);
    setAllocationEdits(null);
  };

  const allocationSeconds = (allocation: Allocation) =>
    Array.from(allocation.entries()).map(([operationId, minutes]) => ({operationId, actualSeconds: minutes * 60}));

  /** Finish and Move Job to Completed: the hand-moved split is saved (else the server derives the even one), then completed. */
  const finishJob = () => void run('Failed to complete job', async () => {
    if (!workOrder) return;
    await clocks.stopAll();
    await flushAll();
    // SQ-636: never from someone the actual times were hidden from; the server derives the even split itself.
    if (allocationEdits && actualsShown) await saveJobAllocation(navigate, workOrder.id, allocationSeconds(currentAllocation(items.trees, pool, rate, allocationEdits, false)));
    await completeWorkOrder(navigate, workOrder.id);
    setWorkOrder(w => w && {...w, status: WorkOrderStatus.Completed, scheduledDate: null, scheduledStartTime: null});
    setReview(false);
    setAllocationEdits(null);
    setUseStored(true);
    setEditing(false);
    // The saved allocation comes back on the operations.
    items.refreshAllTrees();
    changed();
    enqueueSnackbar('Job completed.', {variant: 'success', autoHideDuration: 3000});
  });

  /** Saving out of Edit: in review what was saved is the new baseline; a completed job saves its split, behind the crew check. */
  const saveJobOnLock = async (): Promise<boolean> => {
    if (!workOrder) return true;
    if (review) {
      items.clearEquipmentActuals();
      await items.flush();
      setAllocationEdits(null);
      return true;
    }
    // SQ-636: whoever the actual times were hidden from sends no split; it would replace a review they were never sent.
    if (!jobCompleted || pool <= 0 || !actualsShown) return true;
    const blockers = finishJobChecks(crewClocks, [], rate, () => '').blockers;
    if (blockers.length > 0) {
      report(blockers.join(' '));
      return false;
    }
    const allocation = currentAllocation(items.trees, pool, rate, allocationEdits, useStored);
    const stored = storedAllocation(items.trees, pool);
    // The split the server holds is not sent back: saving it would stamp a derived split as a review (hand-placed).
    if (stored && sameAllocation(stored, allocation)) {
      setAllocationEdits(null);
      setUseStored(true);
      return true;
    }
    try {
      await saveJobAllocation(navigate, workOrder.id, allocationSeconds(allocation));
      setAllocationEdits(null);
      setUseStored(true);
      items.refreshAllTrees();
    } catch (e) {
      if (!isAuthRedirectError(e)) report(errorText(e, 'Failed to save the time allocation'));
    }
    return true;
  };

  /** Create the Invoice: cut at once, no confirmation and no send; the invoice opens (SQ-598 brings it onto this page). */
  const createInvoice = () => void run('Failed to create invoice', async () => {
    if (!workOrder) return;
    const invoice = await createJobInvoice(navigate, workOrder.id);
    setInvoiceId(invoice.id);
    setWorkOrder(w => w && {...w, status: WorkOrderStatus.Invoiced});
    changed();
    enqueueSnackbar('Invoice created.', {variant: 'success', autoHideDuration: 3000});
    navigate(`/invoices/${invoice.id}`);
  });

  // ---- The invoice (SQ-598) -----------------------------------------------------------------------------

  /** The invoice as the server now has it, with the pop-up if it moved between Paid and not. */
  const applyInvoice = (before: InvoiceDto, after: InvoiceDto, message?: string) => {
    const notice = invoiceStatusMoveNotice(before.status, after.status, after.estimate?.totalCost ?? 0, after.amountPaid ?? 0, money);
    setInvoice(after);
    if (notice) setInvoiceNotice(notice);
    else if (message) enqueueSnackbar(message, {variant: 'success', autoHideDuration: 3000});
  };

  const refreshInvoice = async (before: InvoiceDto) => {
    try {
      applyInvoice(before, await getInvoice(navigate, before.id));
    } catch (e) {
      if (!isAuthRedirectError(e)) logError('Error refreshing the invoice', {invoiceId: before.id}, e as Error);
    }
  };

  /** Record Payment: one more payment on file; the server marks the invoice Paid once they cover it. */
  const recordPayment = (amount: number) => void run('Failed to record the payment', async () => {
    if (!invoice) return;
    if (!Number.isFinite(amount) || amount <= 0) {
      report('Enter the amount the customer paid.');
      return;
    }
    const updated = await recordInvoicePayment(navigate, invoice.id, amount);
    // A Created invoice paid in full gets the pop-up (Moved to Paid); a Sent one the message, as on Android.
    applyInvoice(invoice.status === 'Created' ? invoice : updated, updated,
      updated.status === 'Paid' ? 'Payment recorded. The invoice is paid in full.' : 'Payment recorded.');
    changed();
  });

  /** Send Invoice / Send Receipt: the server's own message for it, to the checked recipients. */
  /**
   * SQ-745: the send block reads the client card as it is typed (as Android does), so an edit to the primary
   * phone or email made in edit mode is saved before anything is sent to it. False: the save was refused,
   * its message is up, and nothing is sent.
   */
  const saveClientEdits = async () => !editing || !canEditClient || !!(await clientEditing.saveAll());

  const sendInvoiceDocument = (kind: 'invoice' | 'receipt', emails: string[], phones: string[]) =>
    void run(kind === 'invoice' ? 'Failed to send the invoice' : 'Failed to send the receipt', async () => {
      if (!invoice) return;
      if (emails.length === 0 && phones.length === 0) {
        report('Add at least one recipient');
        return;
      }
      // The customer's copy is built from what is saved: what is waiting goes first.
      await flushAll();
      if (!(await saveClientEdits())) return;
      const content = await getMessageContent(navigate, kind, invoice.id);
      await sendCommunication(navigate, {
        documentType: kind, documentId: invoice.id, deliveryChannels: [...(emails.length ? ['email'] : []), ...(phones.length ? ['sms'] : [])] as never,
        recipients: [...emails.map(email => ({email})), ...phones.map(phoneNumber => ({phoneNumber}))] as never,
        subject: content.subject, emailBody: content.emailBody, smsBody: content.smsBody,
      });
      setInvoice(await getInvoice(navigate, invoice.id).catch(() => invoice));
      setSentTick(t => t + 1);
      changed();
      enqueueSnackbar(kind === 'invoice' ? 'Invoice sent.' : 'Receipt sent.', {variant: 'success', autoHideDuration: 3000});
    });

  const scrollTo = (ref: React.RefObject<HTMLDivElement>) => ref.current?.scrollIntoView({behavior: 'smooth', block: 'start'});

  /**
   * Edit unlocks (and moves a legacy stump waiver from the work item note onto
   * its Stump Grinding operation); Save sends what is waiting, writes the
   * client, and locks.
   */
  const toggleEdit = () => {
    if (!editing) {
      if (!canUnlock) return;
      setEditing(true);
      // A job's price was agreed: it keeps its pricing rules, and the first change that moves its total asks (SQ-530).
      priceConfirmed.current = false;
      if (!job) void refreshPricing();
      if (company) {
        const waiver = stumpGrindingSeedNote(company.name).trim();
        items.trees.filter(t => t.notes?.trim() === waiver).forEach(tree => {
          const stump = tree.operations.find(o => o.operationType === TreeOperationType.Stump_Grinding);
          if (!stump) return;
          const existing = stump.notes ?? '';
          if (!existing.includes(waiver)) items.changeOperationNotes(tree.id, stump.id, existing.trim() ? `${existing.trimEnd()}\n\n${waiver}` : `\n\n${waiver}`);
          items.changeTree(tree.id, t => ({...t, notes: ''}));
        });
      }
      return;
    }
    void run(job ? 'Failed to save the job' : 'Failed to save the estimate', async () => {
      await flushAll();
      if (canEditClient && !(await clientEditing.saveAll())) return;
      if (job && !(await saveJobOnLock())) return;
      setEditing(false);
      setInfoOpen(false);
      // The server moved the invoice's status the moment the total crossed what was paid: ask for it again.
      if (invoice) await refreshInvoice(invoice);
      changed();
    });
  };

  // ---- The appointment (autosaves, as on its own page) --------------------------------------------

  const latestAppt = useRef({time: apptTime, notes: apptNotes});
  latestAppt.current = {time: apptTime, notes: apptNotes};
  const scheduleAppointment = () => {
    if (!appointment) return;
    lanes.schedule('appointment', async () => {
      await updateAppointment(navigate, appointment.id, {time: latestAppt.current.time, notes: latestAppt.current.notes.trim()});
    });
  };

  // ---- Crew --------------------------------------------------------------------------------------

  const persistCrew = useCallback(async (ids: number[]) => {
    const rows = await setEstimateCrewMembers(navigate, estimateId, {companyCrewMemberIds: ids});
    setCrewRows(rows);
    // The crew's rates price the labor; the server re-derived every operation's minutes from its price at the new rate.
    let rate: number | null = null;
    if (rows.length > 0 && rows.every(r => r.hourlyRate != null)) rate = rows.reduce((s, r) => s + r.hourlyRate!, 0) / rows.length;
    else if (rows.length > 0) rate = (await getEstimate(navigate, estimateId).catch(() => undefined))?.laborRatePerHour ?? null;
    items.setCrew(rows.length, rate);
    items.refreshAllTrees();
    // A job: everyone picked is on the job's crew too, so their clocks have a row (seedMissingJobCrewMembers).
    if (workOrderRef.current) await clocks.seedMissing(ids);
  }, [navigate, estimateId, items, clocks]);

  const toggleCrew = (id: number, confirmed = false) => {
    const removing = selectedCrew.includes(id);
    // SQ-530: someone with tracked time comes off only on purpose, and their time comes off the job with them.
    if (job && removing && !confirmed && (clocks.secondsOf(id) > 0 || clocks.isRunning(id))) {
      setJobDialog({kind: 'remove', id, name: nameOf(id), minutes: secondsToMinutes(clocks.secondsOf(id)), running: clocks.isRunning(id)});
      return;
    }
    if (job && removing) {
      setAllocationEdits(null);
      clocks.remove(id).catch(e => report(errorText(e, 'Failed to take their time off the job')));
    }
    const candidate = candidates.find(c => c.companyCrewMemberId === id);
    // A member with no billable rate cannot be picked: there is nothing to lock their time's price at.
    if (!removing && candidate && !candidate.selectable) {
      setRatePrompt(candidate);
      return;
    }
    const next = removing ? selectedCrew.filter(x => x !== id) : [...selectedCrew, id];
    setSelectedCrew(next);
    items.setCrew(next.length, items.state.laborRatePerHour);
    lanes.schedule('crew', () => persistCrew(next));
  };

  // ---- Cost items --------------------------------------------------------------------------------

  const latestCosts = useRef(costItems);
  latestCosts.current = costItems;
  // SQ-636: cost items are prices too; where they were hidden, every change is refused and says why.
  const changeCost = (id: number, patch: Partial<EstimateCostItemDto>) => {
    if (items.refusesTimeAndPrice()) return;
    setCostItems(list => list.map(i => i.id === id ? {...i, ...patch} : i));
    lanes.schedule(`cost-${id}`, async () => {
      const item = latestCosts.current.find(i => i.id === id);
      if (item) await updateCostItem(navigate, estimateId, id, {description: item.description, price: item.price});
    });
  };
  const addCost = () => items.refusesTimeAndPrice() ? Promise.resolve() : createCostItem(navigate, estimateId, {description: '', price: 0})
    .then(created => setCostItems(list => [...list, created]))
    .catch(e => report(errorText(e, 'Failed to add cost item')));
  const deleteCost = (id: number) => {
    if (items.refusesTimeAndPrice()) return;
    lanes.cancel(`cost-${id}`);
    deleteCostItem(navigate, estimateId, id)
      .then(() => setCostItems(list => list.filter(i => i.id !== id)))
      .catch(e => report(errorText(e, 'Failed to delete cost item')));
  };

  // ---- Work items and gates added from the page ---------------------------------------------------

  const occupied = () => [
    ...items.trees.map(t => ({lat: t.lat, lng: t.lng})),
    ...(estimate?.gates ?? []).map(g => ({lat: g.lat, lng: g.lng})),
    ...(estimate?.additionalComponents ?? []).map(c => ({lat: c.lat, lng: c.lng})),
  ];

  const addWorkItem = (type: WorkItemType) => {
    if (!estimate) return;
    setOpenGateId(undefined);
    const spawn = nextOpenLocation({lat: estimate.lat, lng: estimate.lng}, occupied());
    void items.addWorkItem(type, spawn.lat, spawn.lng);
  };

  /**
   * Gate (SQ-527): a gate on the map clear of everything plotted, and its panel open under the add row
   * (SQ-745, as Android): work items, pictures, width, delete. The panel closes on Close, on delete, when
   * another work item is added, or once scrolled out of sight; after that the gate is worked on from the map.
   */
  const addGate = () => {
    if (!estimate) return;
    void siteMap.addGate(created => setOpenGateId(created.id));
  };

  // ---- Footer actions --------------------------------------------------------------------------------

  const trees = items.trees;
  const missing = trees.filter(t => t.operations.length === 0).length;

  const complete = (force = false) => void run('Failed to complete estimate', async () => {
    if (missing > 0) {
      report(`Every work item needs at least one operation before completing (${missing} missing).`);
      return;
    }
    await flushAll();
    if (!force && aiPricing) {
      const ai = await getAiEstimationStatus(navigate, estimateId);
      if (ai.aiEstimatedCostGenerationStatus === 'InProgress' || ai.aiEstimatedCostGenerationStatus === 'InProgressWithFailures') {
        setDialog('ai-running');
        return;
      }
    }
    await transition(EstimateStatus.EstimateCompleted);
    setEditing(false);
    setInfoOpen(false);
  });

  const reopen = () => void run('Failed to reopen estimate', async () => {
    await transition(EstimateStatus.EstimateInProgress);
    setEditing(true);
    void refreshPricing();
  });

  const setCancelled = (cancel: boolean) => void run(cancel ? 'Failed to cancel estimate' : 'Failed to uncancel estimate', async () => {
    if (cancel) {
      await transition(EstimateStatus.EstimateCancelled);
      setEditing(false);
      setCustomerView(false);
    } else {
      // Back to In Progress, always: a canceled estimate is no longer with the customer.
      await transition(EstimateStatus.EstimateInProgress);
      setEditing(true);
    }
  });

  /**
   * Approval made the estimate a job (the server creates the work order before it answers): the page
   * becomes the job's, under Jobs, as on Android (SQ-745). Replacing the entry keeps Back off the
   * estimate's page, which reads as an estimate no longer. Should the job not be found, the page
   * stays here and turns into it in place.
   */
  const openAsJob = async () => {
    changed();
    setEditing(false);
    setCustomerView(false);
    setSigning(false);
    const found = await getWorkOrders(navigate).then(all => all.find(w => w.estimateId === estimateId)).catch(() => undefined);
    const seesJobs = hasPermission(PermissionCode.JobsViewAll) || hasPermission(PermissionCode.JobsViewAssigned);
    if (found && workOrderId === undefined && seesJobs) {
      navigate(`/jobs/${found.id}`, {replace: true});
      return;
    }
    await reload();
    setJobLookupDone(true);
    if (found) setWorkOrder(found);
  };

  /**
   * SQ-745: the estimate's status changed elsewhere (the customer approved it from their link, someone
   * else's phone): read it again, and an approval opens the job, as this page's own approval does. The
   * server says so over SignalR; a return to the tab asks too, since the ping reaches only the clients
   * connected to the API task that made the change. Nothing happens while this page's own action is
   * under way, or when the status is what the page already shows.
   */
  const live = useRef({status, working, job, openAsJob, adopt});
  live.current = {status, working, job, openAsJob, adopt};
  useEffect(() => {
    let active = true;
    const check = async () => {
      if (!active || live.current.working || live.current.status === undefined) return;
      const fresh = await getEstimate(navigate, estimateId).catch(() => undefined);
      const now = live.current;
      if (!active || !fresh || now.working || fresh.status === now.status) return;
      if (!now.job && !PRE_JOB.includes(fresh.status)) await now.openAsJob();
      else now.adopt(fresh);
    };
    RealTimeService.getInstance().onEstimateStatusChanged(id => { if (id === estimateId) void check(); });
    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onVisible);
      RealTimeService.getInstance().onEstimateStatusChanged(() => {});
    };
  }, [estimateId, navigate]);

  const confirmManually = () => void run('Failed to confirm estimate', async () => {
    await transition(EstimateStatus.EstimateApproved);
    await openAsJob();
  });

  const sign = (file: File) => void run('Failed to confirm estimate', async () => {
    await flushAll();
    await approveEstimateWithSignature(navigate, estimateId, file);
    await openAsJob();
  });

  const setPending = () => void run('Failed to set the estimate to Pending Approval', () => transition(EstimateStatus.EstimatePendingApproval));

  const send = (emails: string[], phones: string[]) => void run('Failed to send estimate', async () => {
    // The customer's document is built server-side from what is saved: what is waiting goes first.
    await flushAll();
    if (!(await saveClientEdits())) return;
    const content = await getMessageContent(navigate, 'estimate', estimateId);
    await sendCommunication(navigate, {
      documentType: 'estimate', documentId: estimateId, deliveryChannels: [...(emails.length ? ['email'] : []), ...(phones.length ? ['sms'] : [])] as never,
      recipients: [...emails.map(email => ({email})), ...phones.map(phoneNumber => ({phoneNumber}))] as never,
      subject: content.subject, emailBody: content.emailBody, smsBody: content.smsBody,
    });
    // Sending moves a Completed, Pending Re-send or Canceled estimate to Pending Approval; a re-send leaves it be.
    if (status && ([EstimateStatus.EstimateCompleted, EstimateStatus.EstimatePendingReSend, EstimateStatus.EstimateCancelled] as string[]).includes(status)) {
      await transition(EstimateStatus.EstimatePendingApproval);
    }
    setSentTick(t => t + 1);
    enqueueSnackbar('Estimate sent.', {variant: 'success', autoHideDuration: 3000});
  });

  /**
   * Show Comparables & Generate AI Price: fresh AI suggestions for the
   * current crew (none without a crew: it asks for one), and every
   * operation that has a suggestion switches to it; the AI rows show.
   */
  const generateAi = () => {
    if (!aiPricing) return;
    // SQ-636: new suggestions move the time and prices; refused before asking for a crew where they are hidden.
    if (items.refusesTimeAndPrice()) return;
    if (selectedCrew.length === 0) {
      setDialog('ai-crew');
      return;
    }
    regenerateAiSuggestions(navigate, estimateId)
      .catch(e => report(errorText(e, 'Failed to generate AI suggestions')));
    items.markAiRequested();
    trees.forEach(t => t.operations
      .filter(o => o.aiEstimatedMinutes != null && o.estimatedMinutesOverride != null)
      .forEach(o => void items.chooseAi(t.id, o.id)));
  };

  // ---- Photos -------------------------------------------------------------------------------------------

  const savePhoto = (id: number, edit: PhotoEdit) => {
    if (!photo) return;
    setSavingPhoto(true);
    items.replacePhoto(photo.treeId, id, edit)
      .then(() => setPhoto(undefined))
      .catch(e => report(errorText(e, 'Error updating image')))
      .finally(() => setSavingPhoto(false));
  };

  /** The viewer's Edit Picture: the item's own photo in place, a connected gate's on its gate (every item it shows on keeps it). */
  const saveViewerPhoto = async (treeId: number, p: Photo, edit: PhotoEdit) => {
    if (p.gateId !== undefined && p.gateImageId !== undefined) return siteMap.replaceGatePhoto(p.gateId, p.gateImageId, edit);
    if (p.imageId === undefined) return;
    try {
      await items.replacePhoto(treeId, p.imageId, edit);
    } catch (e) {
      report(errorText(e, 'Error updating image'));
      throw e;
    }
  };

  // ---- Numbers -------------------------------------------------------------------------------------------

  const {crewSize, crewSelected, aiRequested} = items.state;
  const times = useMemo(() => wallTimes(trees, crewSize, rate), [trees, crewSize, rate]);
  const allEquipment = trees.flatMap(t => t.equipment ?? []);
  const topUps = minimumTopUps(allEquipment);
  const equipmentCost = allEquipment.reduce((s, r) => s + rowCost(r), 0);
  const equipmentWithTopUps = equipmentCost + topUps.reduce((s, t) => s + t.topUp, 0);
  const laborCost = trees.flatMap(t => t.operations).reduce((s, o) => s + (o.estimatedCost ?? 0), 0);
  const costTotal = costItems.reduce((s, i) => s + i.price, 0);
  const pricingInput = {
    laborCost, laborMinutes: times.job * crewSize, crewCount: crewSelected ? crewSize : 0,
    equipmentCost: equipmentWithTopUps, costItemsTotal: costTotal, travelMiles: estimate?.travelMiles ?? null,
  };
  const pricing = applyJobPricing(pricingInput, estimate?.pricingRules);
  const total = jobPricingTotal(pricingInput, pricing);
  const hasOperations = trees.some(t => t.operations.length > 0);

  const locked = !editing || customerView;
  const ctx: RecordContext = {
    rate, crewSize, crewSelected, itemMinutes: times.items, operationMinutes: times.operations, aiRequested: aiPricing && aiRequested,
    aiPricing, customer: customerView, pricesHidden, companyEquipment: items.state.companyEquipment,
    minimumMachines: new Set(topUps.map(t => t.equipmentId)),
  };

  // ---- The job's numbers (SQ-597) ---------------------------------------------------------------------

  const reviewing = jobOpen && review && !customerView;
  const completedEditing = jobCompleted && editing && !customerView;
  // Review starts from the even split; a completed job shows what it saved while that still fits.
  const allocation = useMemo(() => currentAllocation(trees, pool, rate, allocationEdits, !reviewing && useStored),
    [trees, pool, rate, allocationEdits, reviewing, useStored]);
  const jobTimes = useMemo(() => allocateJobTime(trees, pool, crewSize, rate, allocation), [trees, pool, crewSize, rate, allocation]);
  // The equipment's over/under is the bid summary: not to whoever the actual times were hidden from (SQ-636).
  const showOverUnder = (reviewing || jobCompleted) && actualsShown;
  const jobEquipment = jobEquipmentOverUnder(allEquipment);
  const canMoveTime = trees.filter(t => t.operations.length > 0).length > 1;
  const itemLabel = (t: EstimateTreeDto) => `${WORK_ITEM_LABEL[t.workItemType] ?? t.workItemType} #${trees.findIndex(x => x.id === t.id) + 1}`;
  const finishCheck = () => finishJobChecks(crewClocks, trees, rate, itemLabel);
  const storedSplit = completedEditing && useStored ? storedAllocation(trees, pool) : undefined;
  const adjusted = allocationEdits !== null || (!!storedSplit && !sameAllocation(storedSplit, proportionalAllocation(trees, pool, rate)));

  /** Time moved on a tan card; refused, and said why, for whoever the actual times were hidden from (shown no cards). */
  const moveTime = (tree: typeof trees[number], delta: number) => {
    if (!actualsShown) {
      report(ALLOCATION_HIDDEN);
      return;
    }
    const current = tree.operations.reduce((sum, op) => sum + (allocation.get(op.id) ?? 0), 0);
    setAllocationEdits(moveWorkItemTime(trees, allocation, rate, tree.id, current + delta * crewSize));
  };

  /**
   * The tan card under a work item's total: in review, and on a completed job being corrected; to someone sent the actuals.
   * With no crew time there is nothing to move between work items, so there is no card (SQ-745, as Android's noCrewTime).
   */
  const allocationCard = (tree: typeof trees[number]) => actualsShown && tree.operations.length > 0 && !jobTimes.job.noCrewTime && (
    <TimeAllocationCard wallMinutes={jobTimes.byWorkItem.get(tree.id)?.wallMinutes ?? 0} crew={crewSize} canMove={canMoveTime}
                        adjusted={adjusted} target={itemLabel(tree)}
                        onStep={delta => moveTime(tree, delta)}
                        onResetAll={() => setJobDialog({kind: 'reset'})}/>
  );

  const jobFor = (tree: typeof trees[number]): WorkItemJob | undefined => {
    if (!job || customerView) return undefined;
    return {
      equipmentOver: showOverUnder ? equipmentOverUnder(tree.equipment ?? []) : undefined,
      review: reviewing ? {
        workPlan: <WorkPlanEdit tree={tree} items={items} review judged={actualsShown}/>,
        allocation: allocationCard(tree),
      } : undefined,
      // What the time came to and Was It Bid Right?: to someone sent the actual times (SQ-636).
      completed: jobCompleted && actualsShown ? {byOperation: jobTimes.byOperation, item: jobTimes.byWorkItem.get(tree.id)} : undefined,
    };
  };

  /** Edit on a job: every change that moves the total goes through priceChange (SQ-530). */
  const editItems: WorkItems = useMemo(() => {
    if (!job) return items;
    const rowOf = (treeId: number, equipmentId: number) => items.state.equipmentByTree[treeId]?.find(r => r.equipmentId === equipmentId);
    const billedAt = (treeId: number, equipmentId: number, minutes: number) => {
      const row = rowOf(treeId, equipmentId);
      return row ? rowCost({...row, durationMinutes: Math.max(0, minutes)}) : 0;
    };
    const costOfTree = (treeId: number) => {
      const tree = items.trees.find(t => t.id === treeId);
      return (tree?.operations ?? []).reduce((s, o) => s + (o.estimatedCost ?? 0), 0) + (tree?.equipment ?? []).reduce((s, r) => s + rowCost(r), 0);
    };
    return {
      ...items,
      stepOperation: (t, o, d) => priceChange(() => items.stepOperation(t, o, d)),
      stepTree: (t, d) => priceChange(() => items.stepTree(t, d)),
      chooseAi: async (t, o) => priceChange(() => void items.chooseAi(t, o)),
      setEquipmentMinutes: (t, e, m) => {
        const row = rowOf(t, e);
        if (!row || billedAt(t, e, m) === rowCost(row)) items.setEquipmentMinutes(t, e, m);
        else priceChange(() => items.setEquipmentMinutes(t, e, m));
      },
      stepEquipment: (t, e, d) => {
        const row = rowOf(t, e);
        if (!row || billedAt(t, e, row.durationMinutes + d) === rowCost(row)) items.stepEquipment(t, e, d);
        else priceChange(() => items.stepEquipment(t, e, d));
      },
      removeEquipment: (t, e) => {
        const row = rowOf(t, e);
        if (!row || rowCost(row) === 0) items.removeEquipment(t, e);
        else priceChange(() => items.removeEquipment(t, e));
      },
      deleteOperation: async (t, o) => {
        const op = items.trees.find(x => x.id === t)?.operations.find(x => x.id === o);
        if (!op?.estimatedCost) await items.deleteOperation(t, o);
        else priceChange(() => void items.deleteOperation(t, o));
      },
      deleteWorkItem: async t => {
        if (costOfTree(t) === 0) return items.deleteWorkItem(t);
        priceChange(() => void items.deleteWorkItem(t));
        return false;
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, job]);

  // The card a map tap or a new work item brought forward scrolls into view.
  const cards = useRef(new Map<number, HTMLElement>());
  useEffect(() => {
    const id = items.state.focusedTreeId;
    if (id === undefined) return;
    const frame = requestAnimationFrame(() => {
      cards.current.get(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
      items.consumeFocus();
    });
    return () => cancelAnimationFrame(frame);
  }, [items, items.state.focusedTreeId]);
  const cardRef = (id: number) => (el: HTMLElement | null) => { if (el) cards.current.set(id, el); else cards.current.delete(id); };

  // ---- The map ----------------------------------------------------------------------------------------------

  // The record's Edit unlocks the map and Done locks it again, as Android's lock does (SQ-621).
  // Only the page's lock: editing is true only where canUnlock let Edit through (SQ-668), on any record.
  const mapEditable = editing && !customerView;
  const siteMap = useSiteMap({
    navigate, estimateId, estimate, setEstimate, trees,
    moveWorkItem: items.moveWorkItem, setDragDistanceFeet: items.setDragDistanceFeet,
    editable: mapEditable, onError: report,
  });
  // The gate whose panel is open on the record; gone (deleted, or a load without it) closes the panel.
  const openGate = openGateId === undefined ? undefined : siteMap.gates.find(g => g.id === openGateId);
  // One map for every width: it keeps where it was looking (siteMap.view) when it closes or changes place.
  const map = estimate && ((layout: RecordMapLayout) => (
    <JobSiteMap
      siteMap={siteMap}
      estimateLat={estimate.lat}
      estimateLng={estimate.lng}
      trees={trees}
      editable={mapEditable}
      onOpenWorkItem={treeId => {
        // Beside the record its card scrolls into view; full size, the page swaps back to the record at that card.
        layout.showRecord();
        items.focus(treeId);
      }}
    />
  ));

  // ---- The page -------------------------------------------------------------------------------------------------

  const ready = !!estimate && !!client && contactsRead;
  const failed = loadError ?? clientEditing.loadError;
  const question: CreateQuestion = {client: fields, entries, prompt: null, picks: NO_PICKS};
  const card = {
    question, onPick: () => undefined, rating: customerView ? undefined : rating, onStar: clientEditing.tapStar, onSaveReview: clientEditing.saveReview,
    additionalOpen, onToggleAdditional: () => setAdditionalOpen(open => !open), customer: customerView,
  };
  const awaiting = !!status && AWAITING_CONFIRM.includes(status);
  const canChangeCrew = !customerView && (invoicePage ? invoiceEditable && editing : job
    ? canEditJob && (jobOpen || (jobCompleted && editing))
    : canEdit && estimatePhase && !cancelled);
  const editableTotal = !locked && hasOperations;
  ctx.onSelectCrew = canChangeCrew ? () => setCrewPickerOpen(true) : undefined;
  // SQ-660: the job-level timer and the typed Job Total spread by price, so every
  // operation needs one first. The timer also needs crew (asked for first, in
  // red); the Job Total is price only and works without.
  const missingPrice = missingPriceReason(trees, trees, false, 'time');
  const jobTotalMissingPrice = crewSelected ? missingPrice : missingPriceReason(trees, trees, false, 'a price');
  // The clocks: start/stop on an open job; the pencil only while it is reviewed or a completed job is edited (SQ-530).
  const clockControls: 'clock' | 'pencil' | undefined = !job || customerView ? undefined
    : reviewing || (jobCompleted && editing) ? (hasPermission(PermissionCode.JobsEditCrewHours) ? 'pencil' : undefined)
      : jobOpen ? (hasPermission(PermissionCode.JobsTimers) ? 'clock' : undefined) : undefined;
  const jobTimeList = (alone: boolean) => (
    <div className={alone ? 'jb-job-time jb-job-time--alone' : 'jb-job-time'}>
      <JobTimeList crew={crewClocks} controls={clockControls} disabled={working}
                   emptyText={<div className={clockControls ? 'jb-job-time-empty jb-job-time-empty--red' : 'jb-job-time-empty'}>
                     {jobCompleted ? 'No crew time was logged on this job' : 'Select crew members to operate timers'}
                   </div>}
                   onToggle={id => void clocks.toggle(id)} onSetSeconds={setCrewSeconds}/>
    </div>
  );
  // An approved estimate is a job: until its work order loads there is no status to show ("Approved" is not one).
  const jobLoading = !job && !jobLookupDone && !!status && !PRE_JOB.includes(status) && !loadError;
  const shownStatus = invoicePending || jobLoading ? '' : invoice ? invoiceStatusLabel(invoice.status) : job ? recordStatusLabel(woStatus ?? '') : status ? recordStatusLabel(status) : '';
  const canFinish = jobOpen && !editing && hasPermission(PermissionCode.JobsComplete);


  // ---- The photo viewer (SQ-657) ----------------------------------------------------------------------

  const openViewer = (tree: EstimateTreeDto, p: Photo) =>
    setViewing({treeId: tree.id, start: Math.max(0, photosOf(tree, estimate?.gates ?? []).findIndex(x => x.key === p.key))});
  // The item's own photos, then its gates' badged "Gate N", read from the record each render so a saved edit shows at once.
  // An emptied list stays mounted: the viewer closes itself on it.
  const viewerTree = viewing ? trees.find(t => t.id === viewing.treeId) : undefined;
  const viewerPhotos = viewing && viewerTree && {
    treeId: viewerTree.id,
    start: viewing.start,
    photos: photosOf(viewerTree, estimate?.gates ?? []).map(p => ({
      id: p.key, url: p.url, photo: p, imageId: p.imageId ?? p.gateImageId,
      badge: p.gate !== undefined ? `Gate ${p.gate}` : undefined,
      // A gate's photo is removed from this work item only (SQ-716, as Android's); its own photo is deleted.
      deleteConfirm: p.gateId !== undefined ? hideGatePhotoConfirm(p.gate) : undefined,
    })),
  };
  return (
    <RecordPage
      name="EstimateRecordPage"
      className="er-page"
      status={shownStatus}
      action={canUnlock && !customerView ? <HeaderButton label={editing ? 'Save' : 'Edit'} disabled={working} onClick={toggleEdit}/> : undefined}
      map={map || undefined}
      mapHidden={customerView}
    >
      {failed && <RecordMessage error text={failed}/>}
      {!ready && !failed && <CircularProgress/>}
      {ready && estimate && <>
        {(invoicePage || invoicePending) && (
          <InvoiceActionBar hidden={invoicePending} onScrollToPayment={() => scrollTo(paymentRef)} onScrollToSend={() => scrollTo(sendRef)}
                            preview={invoicePage ? {on: customerView, onToggle: () => setCustomerView(v => !v)} : undefined}/>
        )}
        {job && !invoicePage && !invoicePending && !customerView && (
          <ActionBar>
            <div className={cancelled ? 'er-action-row er-action-row--end' : 'er-action-row'}>
              {!cancelled && (jobCompleted ? canEditJob : hasPermission(PermissionCode.JobsSchedule)) && (
                <button type="button" className="er-bar-button er-bar-button--green" disabled={working}
                        onClick={() => onJobAction(jobActionOutcome('schedule', woStatus, hasSchedule))}>{scheduleSlotLabel(woStatus)}</button>
              )}
              {!cancelled && !jobCompleted && hasPermission(PermissionCode.JobsHold) && (
                <button type="button" className="er-bar-button er-bar-button--gray" disabled={working}
                        onClick={() => onJobAction(jobActionOutcome('hold', woStatus, hasSchedule))}>
                  {isOnHoldJob(woStatus) ? 'Take Off Hold' : 'Place on Hold'}
                </button>
              )}
              {(cancelled ? canEditJob : hasPermission(PermissionCode.JobsCancel)) && (
                <RedButton label={cancelled ? 'Uncancel' : 'Cancel'} disabled={working}
                           onClick={() => onJobAction(jobActionOutcome('cancel', woStatus, hasSchedule))}/>
              )}
            </div>
          </ActionBar>
        )}
        {estimatePhase && (
          <ActionBar>
            <div className={cancelled ? 'er-action-row er-action-row--end' : 'er-action-row'}>
              {!cancelled && (
                <button type="button" className={customerView ? 'er-bar-button er-bar-button--solid' : 'er-bar-button er-bar-button--green'}
                        onClick={() => { setCustomerView(v => !v); setSigning(false); }}>
                  <VisibilityIcon/>{customerView ? 'Admin View' : 'Preview'}
                </button>
              )}
              {awaiting && canEdit && !customerView && (
                <button type="button" className="er-bar-button er-bar-button--gray" disabled={working} onClick={reopen}>Reopen Est</button>
              )}
              {!customerView && (cancelled ? canEdit : hasPermission(PermissionCode.EstimatesCancel)) && (
                <RedButton label={cancelled ? 'Uncancel' : 'Cancel'} disabled={working}
                           onClick={() => cancelled ? setCancelled(false) : setDialog('cancel')}/>
              )}
            </div>
          </ActionBar>
        )}
        <TitleRow title={invoicePending ? 'Job' : invoice ? `Invoice #${invoice.companyInvoiceId}` : workOrder ? `Job #${workOrder.id}` : `Estimate #${estimate.id}`}
                  dates={invoicePending || customerView ? undefined : invoice ? {createdAt: invoice.createdAt, updatedAt: invoice.updatedAt}
                    : workOrder ? {createdAt: workOrder.createdAt, updatedAt: workOrder.updatedAt} : {createdAt: estimate.createdAt, updatedAt: estimate.updatedAt}}/>
        {company && <CompanyBranding name={company.name} logoUrl={company.logoUrl}/>}
        <div className="ap-client-heading">
          <span className="ap-client-label">CLIENT</span>
          {!customerView && (
            <button type="button" className="ap-view-profile" onClick={() => navigate(`/clients/${estimate.clientId}`)}>
              View profile<span className="ap-chevron" aria-hidden="true">›</span>
            </button>
          )}
        </div>
        {!locked && canEditClient
          ? <ClientEditCard {...card} onEntryChanged={clientEditing.changeEntry} onAddressPicked={clientEditing.pickAddress}
                            onTitleChanged={clientEditing.changeTitle}
                            onAddEntry={kind => { clientEditing.addEntry(kind); setAdditionalOpen(true); }}/>
          : <ClientCard {...card}/>}
        {appointment && !customerView && (
          <AppointmentInfoCard time={apptTime} notes={apptNotes} editing={!locked && hasPermission(PermissionCode.AppointmentsEdit)}
                               open={infoOpen} onToggle={() => setInfoOpen(o => !o)}
                               onTimeChanged={t => { setApptTime(t); scheduleAppointment(); }}
                               onNotesChanged={n => { setApptNotes(n); scheduleAppointment(); }}/>
        )}

        <div className="er-section">
          {job && !customerView && !jobCompleted && hasSchedule && (
            <ScheduledCard date={workOrder?.scheduledDate} time={workOrder?.scheduledStartTime}
                           onSiteMinutes={crewSelected && !pricesHidden ? times.job : undefined}
                           onEdit={hasPermission(PermissionCode.JobsSchedule) && !cancelled ? () => setJobDialog({kind: 'schedule'}) : undefined}/>
          )}
          {reviewing && <div ref={bannerRef} className="jb-banner-slot"><ReviewJobBanner/></div>}
          {!customerView && (!jobCompleted || editing) && (
            <CrewCard roster={roster} selectedIds={selectedCrew} crewRows={crewRows} timeHidden={pricesHidden}
                      jobMinutes={crewSelected ? times.job : undefined} canChange={canChangeCrew} onToggle={id => toggleCrew(id)}
                      jobTime={job && selectedCrew.length > 0 ? jobTimeList(false) : undefined}/>
          )}
          {!customerView && jobCompleted && !editing && <div className="er-info" role="group" aria-label="Job time">{jobTimeList(true)}</div>}

          <div className="er-items">
            {trees.length === 0 && locked && (
              <div className="er-muted">{canUnlock ? 'No work items yet — tap Edit to add one.' : 'No work items.'}</div>
            )}
            {trees.map((tree, i) => locked
              ? <WorkItemCard key={tree.id} tree={tree} number={i + 1} gates={estimate.gates} ctx={ctx} cardRef={cardRef(tree.id)}
                              onOpenPhoto={p => openViewer(tree, p)} job={jobFor(tree)}/>
              : <WorkItemEdit key={tree.id} tree={tree} number={i + 1} gates={estimate.gates} ctx={ctx} items={editItems} companyName={company?.name}
                              cardRef={cardRef(tree.id)} allocation={completedEditing || reviewing ? allocationCard(tree) : undefined} onMessage={text => enqueueSnackbar(text, {variant: 'info', autoHideDuration: 4000})}
                              onOpenPhoto={p => openViewer(tree, p)}
                              onEditPhoto={p => setPhoto({photo: p, treeId: tree.id})}
                              onHideGatePhoto={(gateId, imageId) => void siteMap.hideGatePhoto(gateId, imageId, tree.id)}/>)}
          </div>

          {!locked && (
            <div className="we-below">
              <AddWorkItemRow enabled={!working} onAdd={addWorkItem} onAddGate={addGate}/>
              {openGate && (
                <ClosesOutOfSight onGone={() => setOpenGateId(undefined)}>
                  <GatePanel gate={openGate} className="er-gate-panel"
                             number={localIds(siteMap.gates.map(g => g.id)).get(openGate.id) ?? 0}
                             uploads={siteMap.gateUploads[openGate.id] ?? []}
                             trees={trees} treeNumbers={localIds(trees.map(t => t.id))} canEdit
                             actions={gatePanelActions(siteMap, openGate.id, () => setOpenGateId(undefined))}
                             footer={(
                               <div className="er-gate-note">
                                 Gate {localIds(siteMap.gates.map(g => g.id)).get(openGate.id)} has been added to the map. This panel closes when you
                                 leave it; to make changes later, tap the gate on the map.
                               </div>
                             )}/>
                </ClosesOutOfSight>
              )}
              <div className="er-footer-rule"/>
              <CostItemsEdit items={costItems}
                             onChange={(id, patch) => patch.price !== undefined && patch.price !== costItems.find(c => c.id === id)?.price
                               ? priceChange(() => changeCost(id, patch)) : changeCost(id, patch)}
                             onAdd={() => void addCost()}
                             onDelete={id => costItems.find(c => c.id === id)?.price ? priceChange(() => deleteCost(id)) : deleteCost(id)}/>
            </div>
          )}

          <div className="er-footer-rule"/>
          {/* SQ-636: no Job Total card where the prices were hidden from the person (its actual time goes with them). */}
          {/* SQ-337: the customer sees the record's total alone; an invoice adds what was paid and what is due. */}
          {!pricesHidden && customerView && (invoice
            ? <CustomerInvoiceTotals total={total} paid={invoice.amountPaid ?? 0} status={invoice.status}/>
            : <CustomerEstimateTotal total={total}/>)}
          {!pricesHidden && !customerView && <div className="er-info er-job-total">
            {!customerView && <>
              {topUps.map(t => <TotalRow key={t.equipmentId} label={`Added to meet ${t.name} minimum`} value={`+${money(t.topUp)}`} amount={t.topUp}/>)}
              <TotalRow label="Total Equipment Cost" value={money(equipmentWithTopUps)} amount={equipmentWithTopUps}
                        tag={showOverUnder && (jobEquipment.coveredByMinimum
                          ? <span className="jb-tag jb-tag--covered">Covered by minimum</span>
                          : <OverUnderTag text={overUnderTag(jobEquipment.dollars)} over={jobEquipment.dollars > 0}/>)}/>
              <TotalRow label="Total Labor Time & Cost" value={money(laborCost)} amount={laborCost}
                        time={!crewSelected ? '—' : times.job > 0 ? formatMinutes(times.job) : undefined}/>
            </>}
            {editableTotal
              ? <TimerPill crewSelected={crewSelected} caption={crewCaption(crewSize)} target="Job Total" onSelectCrew={ctx.onSelectCrew}
                           blockedReason={missingPrice} stepRefusal={d => parentStepRefusal(trees, trees, false, d, crewSize, rate)}
                           onStep={d => priceChange(() => items.stepEstimate(d))}/>
              : !customerView && times.job > 0 && crewSelected && <div className="er-crew-caption">{crewCaption(crewSize)}</div>}
            {!customerView && <>
              {costTotal > 0 && <TotalRow label="Total Additional Cost Items" value={money(costTotal)} amount={costTotal}/>}
              {pricing.smallJobRateAmount > 0 && <TotalRow label="Added for small job rate" value={money(pricing.smallJobRateAmount)} amount={pricing.smallJobRateAmount}/>}
              {pricing.minimumTopUpAmount > 0 && <TotalRow label="Added to meet job minimum" value={money(pricing.minimumTopUpAmount)} amount={pricing.minimumTopUpAmount}/>}
              {pricing.travelAmount > 0 && <TotalRow label="Added for travel" value={money(pricing.travelAmount)} amount={pricing.travelAmount}/>}
            </>}
            {/* SQ-660: no crew needed (it moves prices; the times follow once crew is picked), but every operation needs a price. */}
            <TotalRow strong label="Job Total" amount={total} value={editableTotal
              ? <EditableJobTotal total={total} fixed={equipmentWithTopUps + costTotal} onSetLaborTotal={l => priceChange(() => items.setLaborTotal(l))}
                                  blockedReason={jobTotalMissingPrice} laborRefusal={l => laborTotalRefusal(trees, l)}/>
              : money(total)}/>
            {/* Where the payments stand against the total; they move with every change (SQ-401). */}
            {invoice && !customerView && <InvoiceStandingLines invoice={invoice} total={total}/>}
            {/* With no crew time it says so (SQ-559), as Android's job card does. */}
            {jobCompleted && !customerView && !editing && actualsShown && (
              <ActualReadout layer="info" title="Actual Time It Took (Total on the Job)" scope={jobTimes.job}/>
            )}
          </div>}

          {job && !customerView && !jobCompleted && (
            <div className="er-info jb-tracked" role="group" aria-label="Tracked job time">{jobTimeList(true)}</div>
          )}
          {canFinish && (reviewing
            ? <ReviewFinishFooter disabled={working} onFinish={finishJob} onGoBack={exitReview}/>
            : <FinishJobFooter check={finishCheck} disabled={working} onReview={enterReview}/>)}
          {invoice && !customerView && (
            <InvoicePaymentSection invoice={invoice} total={total} working={working} onRecordPayment={recordPayment}
                                   paymentRef={paymentRef} sendRef={sendRef}
                                   sendBlock={(
                                     <SendBlock clientName={fields.name} phone={fields.phoneNumber} email={fields.email}
                                                contacts={contacts} sending={working} label="Send Invoice" sentTick={sentTick}
                                                onAddRecipient={canEditClient ? clientEditing.addContact : undefined}
                                                onSend={(emails, phones) => sendInvoiceDocument('invoice', emails, phones)}
                                                actions={(emails, phones) => <>
                                                  <InvoiceSendButtons invoice={invoice} emails={emails} phones={phones} working={working}
                                                                      onSendInvoice={(e, p) => sendInvoiceDocument('invoice', e, p)}
                                                                      onSendReceipt={(e, p) => sendInvoiceDocument('receipt', e, p)}/>
                                                  {/* SQ-688: the follow-up emails still to come, and Stop / Turn back on. */}
                                                  <InvoiceFollowUpsLine invoice={invoice} working={working}
                                                                        owesMoney={!paymentStanding(total, invoice.amountPaid ?? 0, invoice.status).paidInFull}
                                                                        canChange={hasPermission(PermissionCode.InvoicesSend)}
                                                                        onStoppedChange={stoppedAt => setInvoice(i => i && {...i, followUpsStoppedAt: stoppedAt})}/>
                                                </>}/>
                                   )}/>
          )}
          {job && !customerView && woStatus === WorkOrderStatus.Completed && invoiceId === undefined && (
            <div className="er-footer-stack">
              {canEditJob && <button type="button" className="er-tonal" disabled={working} onClick={() => setJobDialog({kind: 'reopen'})}>Reopen the Job</button>}
              {hasPermission(PermissionCode.InvoicesCreate) && (
                <button type="button" className="er-filled" disabled={working} onClick={createInvoice}>Create the Invoice</button>
              )}
            </div>
          )}

          {/* The signature is reached only through Customer Preview and Confirm, as on Android and iOS; plain Preview offers none. */}
          {customerView && signing && awaiting && <SignatureStep working={working} onSign={sign} onNotNow={() => { setSigning(false); setCustomerView(false); }}/>}
          {/* An invoice's Pay, while a balance is due (SQ-337). */}
          {customerView && invoice && customerInvoiceTotals(total, invoice.amountPaid ?? 0, invoice.status).payable && (
            <PayBlock companyId={invoice.companyId}/>
          )}

          {!customerView && status === EstimateStatus.EstimateInProgress && (canEdit ? <>
            {/* SQ-712: the web has no comparables view yet, so without AI pricing the card has nothing to do. */}
            {!locked && aiPricing && <ComparablesCard onGenerate={generateAi}/>}
            <FooterCard>
              <MapFirstNote/>
              <button type="button" className="er-filled" disabled={working || trees.length === 0} onClick={() => complete()}>
                Finish and Complete the Estimate
              </button>
              {missing > 0 && <div className="er-small er-error">{missing} work item(s) still need an operation.</div>}
            </FooterCard>
          </> : null)}

          {!customerView && awaiting && (
            <div className="er-footer-stack">
              {canEdit && <button type="button" className="er-outlined" disabled={working} onClick={reopen}>Reopen the Estimate and Put It Back in Progress</button>}
              {hasPermission(PermissionCode.EstimatesApprove) && <>
                <button type="button" className="er-filled" disabled={working} onClick={() => { setCustomerView(true); setSigning(true); }}>
                  Customer Preview and Confirm
                </button>
                <div className="er-tonal-row">
                  <button type="button" className="er-tonal er-tonal--compact" disabled={working} onClick={() => setDialog('manual-confirm')}>Manually Confirm</button>
                  {status === EstimateStatus.EstimateCompleted && canEdit && (
                    <button type="button" className="er-tonal er-tonal--compact" disabled={working} onClick={() => setDialog('pending')}>Set to Pending Approval</button>
                  )}
                </div>
              </>}
              {hasPermission(PermissionCode.EstimatesSend) && (
                <SendBlock clientName={fields.name} phone={fields.phoneNumber} email={fields.email} contacts={contacts}
                           sending={working} label="Send Estimate" sentTick={sentTick} onSend={send}
                           onAddRecipient={canEditClient ? clientEditing.addContact : undefined}/>
              )}
              {/* SQ-745: Stop automatic follow-ups, under Send while the customer has it (Android's EstimateFollowUpsLine). */}
              {hasPermission(PermissionCode.EstimatesSend) && status === EstimateStatus.EstimatePendingApproval && (
                <EstimateFollowUpsLine estimateId={estimateId} sentTick={sentTick} canStop working={working}/>
              )}
            </div>
          )}

          {!customerView && (
            <div className="er-counts">
              {estimate.gates.length} gate(s), {estimate.additionalComponents.length} chipper truck(s), {estimate.dragPaths.length} drag path(s)
            </div>
          )}
        </div>
      </>}

      {viewerPhotos && (
        // Edit Picture and delete only while the record is unlocked, as Android's; a gate's photo leaves this work item only.
        <PhotoViewerWithEditor photos={viewerPhotos.photos} startIndex={viewerPhotos.start} onClose={() => setViewing(undefined)}
                               onSave={locked ? undefined : (p, edit) => saveViewerPhoto(viewerPhotos.treeId, p.photo, edit)}
                               onDelete={locked ? undefined : p => {
                                 if (p.photo.gateId !== undefined && p.photo.gateImageId !== undefined) void siteMap.hideGatePhoto(p.photo.gateId, p.photo.gateImageId, viewerPhotos.treeId);
                                 else if (p.photo.imageId !== undefined) void items.deletePhoto(viewerPhotos.treeId, p.photo.imageId);
                               }}/>
      )}
      <Dialog open={!!photo} onClose={() => setPhoto(undefined)} fullScreen disableEscapeKeyDown PaperProps={PHOTO_DIALOG_PAPER}>
        <DrawableImage image={photo?.photo.imageId !== undefined ? {id: photo.photo.imageId, s3Url: photo.photo.url} : null}
                       readonly={false} isSaving={savingPhoto} onClose={() => setPhoto(undefined)} onSave={savePhoto}/>
      </Dialog>

      {invoiceNotice && <InvoiceNoticeDialog text={invoiceNotice} onClose={() => setInvoiceNotice(undefined)}/>}
      {jobDialog?.kind === 'schedule' && (
        <ScheduleDialog date={workOrder?.scheduledDate} time={workOrder?.scheduledStartTime} onClose={() => setJobDialog(null)}
                        onSave={(date, time) => saveSchedule(date, time)} onCancelSchedule={() => saveSchedule(null, null, true)}/>
      )}
      {jobDialog?.kind === 'reopen' && (
        <SimpleDialog title="Reopen this job?" text={REOPEN_JOB_CONFIRM_MESSAGE} confirm="Reopen" onClose={() => setJobDialog(null)} onConfirm={reopenJob}/>
      )}
      {jobDialog?.kind === 'confirm' && (
        <SimpleDialog title={jobDialog.status === WorkOrderStatus.OnHold ? 'Place on hold?' : 'Cancel this job?'} text={jobDialog.message}
                      confirm="Continue" danger={jobDialog.status === WorkOrderStatus.Cancelled} onClose={() => setJobDialog(null)}
                      onConfirm={() => setJobStatus(jobDialog.status)}/>
      )}
      {jobDialog?.kind === 'notice' && <SimpleDialog title="Not available" text={jobDialog.message} confirm="OK" onClose={() => setJobDialog(null)}/>}
      {jobDialog?.kind === 'price' && (
        <SimpleDialog title={invoiceId !== undefined ? 'Change the invoice total?' : 'Change the job total?'}
                      text={`This changes the ${invoiceId !== undefined ? 'invoice total' : 'job total'} the customer will pay. Do you want to continue?`}
                      confirm="Continue" onClose={() => setJobDialog(null)}
                      onConfirm={() => {
                        priceConfirmed.current = true;
                        const change = pendingPrice.current;
                        pendingPrice.current = null;
                        change?.();
                      }}/>
      )}
      {jobDialog?.kind === 'pool' && (
        <SimpleDialog title="Change crew hours?" confirm="Change Hours" cancel="Keep" onClose={() => setJobDialog(null)}
                      onConfirm={() => setCrewSeconds(jobDialog.id, jobDialog.seconds, true)}
                      text="Changing crew hours will undo the time you moved by hand and set everything back to its original split across the job. You can move time around again afterwards."/>
      )}
      {crewPickerOpen && canChangeCrew && (
        <CrewPickerDialog roster={roster} selectedIds={selectedCrew} onToggle={id => toggleCrew(id)} onClose={() => setCrewPickerOpen(false)}/>
      )}
      {jobDialog?.kind === 'reset' && (
        <SimpleDialog title="Reset all times?" text="All times will be reset to how they were originally split across the job."
                      confirm="Reset" cancel="Keep" onClose={() => setJobDialog(null)}
                      onConfirm={() => { setAllocationEdits(null); setUseStored(false); }}/>
      )}
      {jobDialog?.kind === 'remove' && (
        <SimpleDialog title={`Remove ${jobDialog.name} and their time?`} confirm="Remove" cancel="Keep" onClose={() => setJobDialog(null)}
                      onConfirm={() => toggleCrew(jobDialog.id, true)}
                      text={`${jobDialog.name} has ${formatMinutes(jobDialog.minutes)} tracked on this job${jobDialog.running ? ' and their timer is running' : ''}. Removing them takes that time off the job.`}/>
      )}
      {dialog === 'cancel' && (
        <SimpleDialog title="Cancel estimate?" danger confirm="Cancel Estimate" cancel="Keep" onClose={() => setDialog(null)} onConfirm={() => setCancelled(true)}
                      text="The estimate's status becomes Canceled. It stays on file with everything on it, under the Canceled filter on the Estimates page, and Uncancel brings it back."/>
      )}
      {dialog === 'ai-running' && (
        <SimpleDialog title="AI estimation in progress" confirm="Complete Anyway" cancel="Wait" onClose={() => setDialog(null)} onConfirm={() => complete(true)}
                      text="Some operations are still being priced by AI. If you complete now, those in-progress results will be ignored."/>
      )}
      {dialog === 'ai-crew' && (
        <SimpleDialog title="Select crew members first" confirm="OK" onClose={() => setDialog(null)}
                      text="The AI suggests how long the selected crew will take and prices that time at their rates. Pick the crew for this job, then generate the AI price."/>
      )}
      {dialog === 'manual-confirm' && (
        <SimpleDialog title="Confirm without a signature?" confirm="Confirm" onClose={() => setDialog(null)} onConfirm={confirmManually}
                      text="This confirms the estimate and creates the job, without the customer's signature."/>
      )}
      {dialog === 'pending' && (
        <SimpleDialog title="Set to Pending Approval?" confirm="Set to Pending" onClose={() => setDialog(null)} onConfirm={setPending}
                      text="This moves the estimate to Pending Approval without sending it to the customer."/>
      )}
      {ratePrompt && (
        <SimpleDialog title="Set a billable rate first" confirm="Open profile" cancel="Not now" onClose={() => setRatePrompt(undefined)}
                      onConfirm={() => navigate(`/settings/access/${ratePrompt.kind === 'invite' ? 'invites' : 'users'}/${ratePrompt.recordId}`)}
                      text={`Set a billable rate for ${ratePrompt.name} before using them on an estimate or job. Go to the Team page, open ${ratePrompt.name}'s profile and add their billable rate.`}/>
      )}
    </RecordPage>
  );
};

export default EstimateRecordPage;
